import React, { useEffect, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import mascotNoData from "../../../../Assets/icons/okkali_mascot_nodata.png";
import { useGetFirmConsumentLogReportQuery as defaultUseStatsQuery } from "../../../../store";
import useAuth from "../../../../Hooks/useAuth";
import useFirmScopeSelection, { toScopeFirmOption } from "../../../../Hooks/useFirmScopeSelection";
import { MascotLoadingOverlay } from "../../../shared";
import { FirmDropdown } from "../../../atoms";
import CustomStyles from "../../../../CustomStyles";

// Skeleton Loading Component
const StatsSkeleton = () => {
  return (
    <div className="space-y-4">
      {/* Header Skeleton */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="h-5 bg-gray-200 rounded-lg w-24 sm:w-36 animate-pulse" />
        <div className="flex gap-0.5 sm:gap-1 bg-gray-100 rounded-lg sm:rounded-xl p-0.5 sm:p-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-6 sm:h-8 bg-gray-200 rounded-md sm:rounded-lg w-12 sm:w-16 animate-pulse"
            />
          ))}
        </div>
      </div>

      {/* Ürün Kutuları Skeleton */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-3 sm:p-4 animate-pulse">
            <div className="h-2.5 bg-gray-200 rounded w-14 mb-2" />
            <div className="h-6 bg-gray-200 rounded w-12" />
          </div>
        ))}
      </div>

      {/* Alt Kartlar Skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-xl p-3 space-y-2.5 animate-pulse">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-16" />
              <div className="h-4 bg-gray-200 rounded w-10" />
            </div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-xl p-3 space-y-2 animate-pulse">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i}>
              <div className="flex justify-between mb-1">
                <div className="h-3 bg-gray-200 rounded w-16" />
                <div className="h-3 bg-gray-200 rounded w-12" />
              </div>
              <div className="h-2 bg-gray-200 rounded-full w-full" />
            </div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-xl p-3 animate-pulse sm:col-span-2 lg:col-span-1">
          <div className="h-32 bg-gray-200 rounded-lg" />
        </div>
      </div>
    </div>
  );
};

const StatsCardView = ({
  ProductData,
  productTotals,
  totalConsumption,
  colorOfProduct,
}) => {
  const hourlyTotals = (ProductData || []).map((h, i) => {
    const total = Object.entries(h || {}).reduce(
      (s, [k, v]) => (k !== "name" ? s + (Number(v) || 0) : s),
      0
    );
    return { hour: h?.name || `${String(i).padStart(2, "0")}:00`, total };
  });
  const maxHourly = Math.max(...hourlyTotals.map((h) => h.total), 1);
  const peakHours = [...hourlyTotals]
    .sort((a, b) => b.total - a.total)
    .filter((h) => h.total > 0)
    .slice(0, 5);
  const activeHours = hourlyTotals.filter((h) => h.total > 0).length;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4">
      <div className="flex flex-col gap-3 sm:gap-4">
        {/* Ürün Tüketimleri */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          {productTotals.map((item, i) => (
            <div
              key={i}
              className="rounded-xl p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3 transition-shadow hover:shadow-md"
              style={{
                backgroundColor: `${colorOfProduct[item.productName] || "#94a3b8"}10`,
              }}
            >
              <span
                className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full shrink-0"
                style={{ backgroundColor: colorOfProduct[item.productName] || "#94a3b8" }}
              />
              <div className="min-w-0">
                <p
                  className="text-[10px] sm:text-xs font-medium truncate"
                  style={{ color: `${colorOfProduct[item.productName] || "#94a3b8"}99` }}
                >
                  {item.productName}
                </p>
                <p
                  className="text-base sm:text-lg font-bold"
                  style={{ color: colorOfProduct[item.productName] || "#374151" }}
                >
                  {item.total.toLocaleString("tr-TR")}
                </p>
              </div>
            </div>
          ))}
          {/* Aktif Saat & Ortalama */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3">
            <span className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full shrink-0 bg-emerald-400" />
            <div>
              <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                Aktif Saat
              </p>
              <p className="text-base sm:text-lg font-bold text-gray-700">
                {activeHours}
                <span className="text-[10px] sm:text-xs text-gray-400 font-normal ml-0.5">
                  / 24
                </span>
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3">
            <span className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full shrink-0 bg-amber-400" />
            <div>
              <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                Ort./Saat
              </p>
              <p className="text-base sm:text-lg font-bold text-gray-700">
                {activeHours > 0
                  ? Math.round(totalConsumption / activeHours)
                  : 0}
              </p>
            </div>
          </div>
        </div>

        {/* Alt Kartlar: Yoğun Saatler + Ürün Dağılımı + Saatlik Grafik */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* En yoğun saatler */}
          {peakHours.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
              <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50/80 border-b border-gray-100">
                <span className="text-xs sm:text-sm font-semibold text-gray-600">
                  En Yoğun Saatler
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {peakHours.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 sm:px-4 py-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white ${
                          i === 0
                            ? "bg-amber-500"
                            : i === 1
                            ? "bg-gray-400"
                            : "bg-amber-700"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {h.hour}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 sm:w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full"
                          style={{
                            width: `${(h.total / maxHourly) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm font-bold text-gray-700 min-w-[32px] text-right">
                        {h.total}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ürün Dağılımı */}
          {productTotals.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
              <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50/80 border-b border-gray-100">
                <span className="text-xs sm:text-sm font-semibold text-gray-600">
                  Ürün Dağılımı
                </span>
              </div>
              <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                {productTotals.map((item, i) => {
                  const pct =
                    totalConsumption > 0
                      ? Math.round((item.total / totalConsumption) * 100)
                      : 0;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{
                              backgroundColor:
                                colorOfProduct[item.productName] || "#94a3b8",
                            }}
                          />
                          <span className="text-xs sm:text-sm font-medium text-gray-700">
                            {item.productName}
                          </span>
                        </div>
                        <span className="text-xs sm:text-sm font-bold text-gray-800">
                          {item.total.toLocaleString("tr-TR")}{" "}
                          <span className="text-gray-400 font-normal">
                            (%{pct})
                          </span>
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            backgroundColor:
                              colorOfProduct[item.productName] || "#94a3b8",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Saatlik Grafik */}
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden flex flex-col sm:col-span-2 lg:col-span-1 min-h-[180px] sm:min-h-[220px]">
            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50/80 border-b border-gray-100 shrink-0">
              <span className="text-xs sm:text-sm font-semibold text-gray-600">
                Saatlik Dağılım
              </span>
            </div>
            <div className="p-3 sm:p-4 flex-1 flex flex-col">
              <div className="flex items-end gap-[2px] sm:gap-1 flex-1">
                {hourlyTotals.map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 flex flex-col items-center h-full justify-end"
                  >
                    <div
                      className={`w-full rounded-t-sm transition-all ${
                        h.total > 0
                          ? "bg-indigo-400 hover:bg-indigo-500"
                          : "bg-gray-100"
                      }`}
                      style={{
                        height: `${
                          maxHourly > 0
                            ? Math.max(3, (h.total / maxHourly) * 100)
                            : 3
                        }%`,
                      }}
                      title={`${h.hour}: ${h.total}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 shrink-0">
                <span className="text-[10px] sm:text-xs text-gray-400">00</span>
                <span className="text-[10px] sm:text-xs text-gray-400">06</span>
                <span className="text-[10px] sm:text-xs text-gray-400">12</span>
                <span className="text-[10px] sm:text-xs text-gray-400">18</span>
                <span className="text-[10px] sm:text-xs text-gray-400">23</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatsTab = ({ firm, useStatsQuery }) => {
  const { auth } = useAuth();
  const [active, setActive] = useState(0);
  const [filterType, setFilterType] = useState("today");
  // Alt firma kapsamı — VARSAYILAN: firma + TÜM alt firmaları
  // (kullanıcı kararı 2026-09-14). Ayrıntı: hooks/useFirmScopeSelection.
  const [firmOptions, setFirmOptions] = useState([]);
  const { requestFirmIds, selectedFirms, onChange: onScopeChange, hasSubFirms } =
    useFirmScopeSelection(firmOptions);
  const useGetFirmConsumentLogReportQuery = useStatsQuery || defaultUseStatsQuery;

  const colorOfProduct = CustomStyles.productColors;

  const id = firm?._id;
  const token = auth.accessToken || undefined;

  const response = useGetFirmConsumentLogReportQuery({
    id,
    type: filterType,
    token,
    firmIds: requestFirmIds,
  });

  // Seçici seçenekleri — sunucudan gelen kök + alt firma listesi. Kapsam
  // daraltılınca sunucu yine tümünü döndürür; son bilinen liste state'te
  // tutulur ki yanıt arasında seçici boşalmasın.
  const serverFirms = response?.data?.firms;
  useEffect(() => {
    if (!Array.isArray(serverFirms) || serverFirms.length === 0) return;
    setFirmOptions(
      serverFirms.map(toScopeFirmOption),
    );
  }, [serverFirms]);

  function createProductDataAndBars(sortedHourlyConsumption) {
    if (!sortedHourlyConsumption) {
      return { productData: [], productBars: [] };
    }
    const ProductBarsSet = new Set();
    let ProductData = [];
    for (let i = 0; i < 24; i++) {
      ProductData[i] = { name: `${i.toString().padStart(2, "0")}:00` };
    }
    for (const log of sortedHourlyConsumption) {
      const hourIndex = parseInt(log.createdInfo, 10);
      const productDataItem = {
        name: `${log.createdInfo.padStart(2, "0")}:00`,
      };
      log.consumentInfo.forEach((product) => {
        productDataItem[product.productName] = product.consument;
        ProductBarsSet.add(product.productName);
      });
      ProductData[hourIndex] = productDataItem;
    }
    const ProductBars = Array.from(ProductBarsSet).map((dataKey) => ({
      dataKey,
      fill: colorOfProduct[dataKey],
    }));
    return { ProductData, ProductBars };
  }

  function calculateProductTotals(sortedHourlyConsumption) {
    if (!sortedHourlyConsumption) return [];
    const productTotalsMap = new Map();
    for (const log of sortedHourlyConsumption) {
      if (log.consumentInfo && Array.isArray(log.consumentInfo)) {
        log.consumentInfo.forEach((product) => {
          const current = productTotalsMap.get(product.productName) || 0;
          productTotalsMap.set(
            product.productName,
            current + (product.consument || 0)
          );
        });
      }
    }
    return Array.from(productTotalsMap.entries())
      .map(([productName, total]) => ({ productName, total }))
      .sort((a, b) => b.total - a.total);
  }

  const sortedHourlyConsumption = response?.data?.data;
  const productTotals = calculateProductTotals(sortedHourlyConsumption);
  const { ProductData } = createProductDataAndBars(sortedHourlyConsumption);

  const totalConsumption = productTotals.reduce((sum, p) => sum + p.total, 0);

  const filterButtons = [
    { type: "today", label: "Bugün" },
    { type: "yesterday", label: "Dün" },
    { type: "week", label: "Haftalık" },
    { type: "thismonth", label: "Aylık" },
  ];

  const renderFilterButtons = () => (
    <div className="flex items-center gap-0.5 sm:gap-1 bg-gray-100 p-0.5 sm:p-1 rounded-lg sm:rounded-xl">
      {filterButtons.map((button, index) => (
        <button
          key={index}
          onClick={() => {
            setFilterType(button.type);
            setActive(index);
          }}
          className={`flex-1 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md sm:rounded-lg transition-all duration-200 text-center ${
            active === index
              ? "bg-indigo-500 text-white shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {button.label}
        </button>
      ))}
    </div>
  );

  const isLoading = response.isFetching || response.isLoading;
  const isEmpty =
    !isLoading &&
    (!sortedHourlyConsumption || sortedHourlyConsumption.length === 0);
  const hasData = !isLoading && !isEmpty;

  return (
    <div className="flex flex-col h-full">
      {/* Dönem filtresi + alt firma seçici (sağda, yalnızca alt firma varsa) */}
      <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-2 shrink-0 flex items-center gap-2">
        <div className="flex-1 min-w-0">{renderFilterButtons()}</div>
        {hasSubFirms && (
          <div className="w-40 sm:w-56 shrink-0">
            <FirmDropdown
              compact
              multiple
              firms={firmOptions}
              value={selectedFirms}
              placeholder="Firma seçin"
              onChange={onScopeChange}
            />
          </div>
        )}
      </div>

      {/* İçerik */}
      {isLoading ? (
        <div className="relative px-3 sm:px-4 py-3 sm:py-4 shrink-0">
          <MascotLoadingOverlay message="Performans verileri yükleniyor..." />
          <StatsSkeleton />
        </div>
      ) : isEmpty ? (
        <div className="flex-1 min-h-0 flex items-center justify-center select-none">
          <div className="text-center px-4">
            <img
              src={mascotNoData}
              alt="Veri bulunamadı"
              className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 object-contain mx-auto mb-4 drop-shadow-lg"
              draggable={false}
            />
            <h3 className="text-base sm:text-lg font-bold text-gray-700 mb-1">
              Veri Bulunamadı
            </h3>
            <p className="text-sm text-gray-400 max-w-xs mx-auto">
              Seçilen tarih aralığında tüketim verisi bulunmuyor.
            </p>
          </div>
        </div>
      ) : hasData ? (
        <StatsCardView
          ProductData={ProductData}
          productTotals={productTotals}
          totalConsumption={totalConsumption}
          colorOfProduct={colorOfProduct}
        />
      ) : null}
    </div>
  );
};

export default StatsTab;
